import { useRef, useState, useSyncExternalStore, type KeyboardEvent } from 'react';
import { Header } from '../Shell';
import { InstallModal, useInstallModal } from '../InstallModal';
import {
  CTA_BLOCK,
  FAQ_BLOCK,
  FOOTER_BLOCK,
  SCENARIOS,
  TRUST_POINTS,
} from '../content';
import { Icon } from '@landing/icons/Icons';
import { u } from '@landing/base';
import { AnimatedWave } from './AnimatedWave';
import { ProductPreview } from './ProductPreview';
import styles from './PrototypeLanding.module.css';

const art = (name: string) => u(`/assets/landing/aura-${name}.webp`);
const motionQuery = '(prefers-reduced-motion: reduce)';
const subscribeToMotion = (callback: () => void) => {
  const query = window.matchMedia(motionQuery);
  query.addEventListener('change', callback);
  return () => query.removeEventListener('change', callback);
};
const getReducedMotion = () => window.matchMedia(motionQuery).matches;

function SectionLabel({ number, children }: { number: string; children: string }) {
  return (
    <p className={styles.sectionLabel}>
      <span>{number}</span>
      {children}
    </p>
  );
}

function Platforms() {
  return (
    <p className={styles.platforms}>
      {[
        ['apple', 'iPhone'],
        ['android', 'Android'],
        ['globe', 'Web'],
      ].map(([icon, label]) => (
        <span key={icon}>
          <Icon name={icon} />
          {label}
        </span>
      ))}
    </p>
  );
}

function InstallButton({
  onClick,
  dark = false,
}: {
  onClick: () => void;
  dark?: boolean;
}) {
  return (
    <button
      type="button"
      className={`${styles.installButton} ${dark ? styles.darkButton : ''}`}
      onClick={onClick}
    >
      Установить AURA<span aria-hidden="true">↗</span>
    </button>
  );
}

function Ticker() {
  const words = ['ЛЮДИ', 'СВЯЗИ', 'ДОВЕРИЕ', 'ВОЗМОЖНОСТИ'];
  return (
    <div className={styles.ticker} aria-hidden="true">
      <div className={styles.tickerTrack}>
        {[0, 1].map((copy) => (
          <span key={copy}>
            {[0, 1, 2].flatMap((repeat) =>
              words.map((word) => (
                <span key={`${repeat}-${word}`}>
                  {word}
                  <i>✦</i>
                </span>
              ))
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

function Product() {
  const [selected, setSelected] = useState(0);
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const scenario = SCENARIOS[selected];
  const selectWithKeyboard = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    const key = event.key;
    if (
      !['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)
    )
      return;
    event.preventDefault();
    const next =
      key === 'Home'
        ? 0
        : key === 'End'
          ? SCENARIOS.length - 1
          : (index +
              (key === 'ArrowDown' || key === 'ArrowRight' ? 1 : -1) +
              SCENARIOS.length) %
            SCENARIOS.length;
    setSelected(next);
    buttons.current[next]?.focus();
  };
  return (
    <section
      id="product"
      className={`${styles.section} ${styles.product}`}
      aria-label="Возможности AURA"
    >
      <div className={styles.inner}>
        <SectionLabel number="02">AURA ПОД РАЗНЫЕ МОМЕНТЫ ЖИЗНИ</SectionLabel>
        <div className={styles.productGrid}>
          <div
            className={styles.scenarioTabs}
            role="tablist"
            aria-label="Возможности приложения"
          >
            {SCENARIOS.map((item, i) => (
              <button
                key={item.id}
                ref={(element) => {
                  buttons.current[i] = element;
                }}
                type="button"
                role="tab"
                id={`scenario-${item.id}`}
                aria-controls="scenario-panel"
                aria-selected={selected === i}
                tabIndex={selected === i ? 0 : -1}
                onClick={() => setSelected(i)}
                onKeyDown={(event) => selectWithKeyboard(event, i)}
              >
                <span>{String(i + 1).padStart(2, '0')}</span>
                {item.name}
                <i aria-hidden="true">✦</i>
              </button>
            ))}
          </div>
          <div
            className={styles.productPanel}
            id="scenario-panel"
            role="tabpanel"
            aria-labelledby={`scenario-${scenario.id}`}
            tabIndex={0}
          >
            <div className={styles.productArt}>
              <span className={styles.productWord} aria-hidden="true">
                {scenario.name}
              </span>
              <div className={styles.productRibbonFrame} aria-hidden="true">
                <img
                  className={styles.productRibbon}
                  src={art('wave')}
                  width="1536"
                  height="1024"
                  alt=""
                  loading="lazy"
                />
              </div>
              <div className={styles.phonePosition}>
                <ProductPreview scenario={scenario.id} title={scenario.name} />
              </div>
              <span className={styles.phoneShadow} aria-hidden="true" />
            </div>
            <div className={styles.productCopy}>
              <p className={styles.eyebrow}>{scenario.name}</p>
              <h2>{scenario.title}</h2>
              <p className={styles.lead}>{scenario.text}</p>
              <div className={styles.scenarioIcons} aria-hidden="true">
                {['heart', 'chat', 'bookmark', 'send'].map((name) => (
                  <Icon key={name} name={name} />
                ))}
              </div>
              <span className={styles.scenarioCount}>
                {String(selected + 1).padStart(2, '0')}
                <i />
                05
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ConnectedSpace() {
  return (
    <section id="advantage" className={`${styles.section} ${styles.connected}`}>
      <div className={styles.inner}>
        <SectionLabel number="03">ВСЁ ВАЖНОЕ — В ОДНОМ ПРОСТРАНСТВЕ</SectionLabel>
        <div className={styles.connectedGrid}>
          <div className={styles.connectedCopy}>
            <h2>
              Всё важное —<br />
              <span>
                в одном
                <br />
                пространстве.
              </span>
            </h2>
            <p className={styles.lead}>
              Люди, связи, задачи, интересы и возможности живут рядом — а не в разных
              приложениях.
            </p>
            <p className={styles.connectedNote}>
              <span />
              Одна AURA. Вокруг вас.
            </p>
          </div>
          <div className={styles.orbitScene}>
            <img
              className={styles.orbit}
              src={art('orbit')}
              width="1024"
              height="1024"
              alt=""
              loading="lazy"
            />
            <span className={styles.orbitCore} aria-hidden="true">
              <Icon name="profile" />
            </span>
            <ul className={`${styles.orbitLabels} ${styles.orbitLeft}`}>
              <li>Люди</li>
              <li>Общение</li>
              <li>Покупки и услуги</li>
              <li>Контент и события</li>
            </ul>
            <ul className={`${styles.orbitLabels} ${styles.orbitRight}`}>
              <li>Знакомства</li>
              <li>Профиль</li>
              <li>Интересы</li>
              <li>Возможности</li>
            </ul>
          </div>
        </div>
        <div id="trust" className={styles.trustPoints}>
          {TRUST_POINTS.map((point, i) => (
            <div key={point.n}>
              <Icon name={['profile', 'star', 'lock', 'shield'][i]} />
              <h3>{point.title}</h3>
              <p>{point.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Portal({ onInstall }: { onInstall: () => void }) {
  return (
    <section id="install" className={`${styles.section} ${styles.portal}`}>
      <div className={styles.inner}>
        <SectionLabel number="04">ВАША AURA НАЧИНАЕТСЯ ЗДЕСЬ</SectionLabel>
        <div className={styles.portalGrid}>
          <div className={styles.portalCopy}>
            <h2>
              ВХОДИТЕ
              <br />В <span>AURA.</span>
            </h2>
            <p className={styles.lead}>{CTA_BLOCK['05'].lead}</p>
            <InstallButton onClick={onInstall} dark />
            <Platforms />
          </div>
          <img
            className={styles.portalArt}
            src={art('portal')}
            width="1024"
            height="1024"
            alt=""
            loading="lazy"
          />
        </div>
        <div className={styles.pillars}>
          {CTA_BLOCK['05'].pillars.map((pillar) => (
            <div key={pillar.title}>
              <Icon name={pillar.icon} />
              <h3>{pillar.title}</h3>
              <p>{pillar.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Faq() {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <section id="faq" className={`${styles.section} ${styles.faq}`}>
      <div className={styles.inner}>
        <SectionLabel number="05">ОТВЕТЫ НА ВАЖНЫЕ ВОПРОСЫ</SectionLabel>
        <div className={styles.faqGrid}>
          <div className={styles.faqIntro}>
            <h2>FAQ</h2>
            <p className={styles.lead}>{FAQ_BLOCK.titles['06'].lead}</p>
            <img
              src={art('crystal')}
              width="1024"
              height="1024"
              alt=""
              loading="lazy"
            />
          </div>
          <div className={styles.questions}>
            {FAQ_BLOCK.items.map((item) => (
              <div className={styles.question} key={item.n}>
                <h3>
                  <button
                    type="button"
                    id={`faq-question-${item.n}`}
                    aria-expanded={open === item.n}
                    aria-controls={`faq-answer-${item.n}`}
                    onClick={() => setOpen(open === item.n ? null : item.n)}
                  >
                    <span>{item.n}</span>
                    {item.q}
                    <i aria-hidden="true">{open === item.n ? '−' : '+'}</i>
                  </button>
                </h3>
                <div
                  id={`faq-answer-${item.n}`}
                  role="region"
                  aria-labelledby={`faq-question-${item.n}`}
                  hidden={open !== item.n}
                >
                  <p>
                    {item.a}
                    {item.n === '05' && (
                      <>
                        {' '}
                        <a href={u('/privacy_policy')}>Подробнее →</a>
                      </>
                    )}
                  </p>
                </div>
              </div>
            ))}
            <div className={styles.faqSupport}>
              <span>06</span>
              <p>Не нашли ответ?</p>
              <a href={FAQ_BLOCK.fallback.href}>
                Написать в поддержку<span aria-hidden="true">↗</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer id="contacts" className={styles.footer}>
      <img
        className={styles.footerWave}
        src={art('wave')}
        alt=""
        width="1536"
        height="1024"
        loading="lazy"
      />
      <div className={`${styles.inner} ${styles.footerGrid}`}>
        <div className={styles.brand}>
          <a href="#top" aria-label="AURA — наверх">
            <img src={u('/aura-logo.png')} width="36" height="36" alt="" />
            <span>aura</span>
          </a>
          <p>{FOOTER_BLOCK.tagline}</p>
        </div>
        <div className={styles.channels}>
          {FOOTER_BLOCK.channels.map((channel) => (
            <a href={channel.href} key={channel.id}>
              <Icon name={channel.icon} />
              <span>
                <b>{channel.title}</b>
                <small>
                  {channel.note}
                  <i aria-hidden="true">↗</i>
                </small>
              </span>
            </a>
          ))}
        </div>
        <nav className={styles.legal} aria-label="Документы">
          {FOOTER_BLOCK.legal.map((link) => (
            <a href={u(link.href)} key={link.href}>
              {link.label}
            </a>
          ))}
        </nav>
        <div className={styles.requisites}>
          {FOOTER_BLOCK.requisites.map((text) => (
            <p key={text}>{text}</p>
          ))}
          <p className={styles.footerMeta}>{FOOTER_BLOCK.meta.join(' · ')}</p>
        </div>
      </div>
    </footer>
  );
}

export function PrototypeLanding() {
  const openInstall = useInstallModal((state) => state.openModal);
  const [paused, setPaused] = useState(false);
  const reduced = useSyncExternalStore(subscribeToMotion, getReducedMotion, () => true);
  const still = paused || reduced;

  return (
    <div className={`v4 ${styles.page}`} data-motion={still ? 'paused' : 'running'}>
      <a className={styles.skipLink} href="#product">
        К содержимому
      </a>
      <Header />
      <main>
        <section id="top" className={styles.hero} aria-labelledby="hero-title">
          <div className={styles.heroGlow} aria-hidden="true" />
          <AnimatedWave src={art('wave')} paused={still} />
          <div className={styles.heroCopy}>
            <p className={styles.heroEyebrow}>ОДНО ПРОСТРАНСТВО. ВОКРУГ ВАС.</p>
            <h1 id="hero-title" className={styles.wordmark}>
              AURA<span>.</span>
            </h1>
            <p className={styles.heroTitle}>Ваш мир. Рядом.</p>
            <p className={styles.heroLead}>
              Всё, что имеет значение вокруг вас,
              <br className={styles.desktopBreak} /> соединяется в одном пространстве.
            </p>
            <InstallButton onClick={openInstall} />
            <Platforms />
          </div>
          <div className={styles.heroBottom}>
            <a href="#product">
              <span aria-hidden="true">↓</span>ОТКРОЙТЕ БОЛЬШЕ
            </a>
            {!reduced && (
              <button
                type="button"
                aria-label={paused ? 'Включить анимацию' : 'Приостановить анимацию'}
                aria-pressed={paused}
                onClick={() => setPaused(!paused)}
              >
                <svg viewBox="0 0 20 20" aria-hidden="true">
                  {paused ? <path d="m7 5 7 5-7 5z" /> : <path d="M7 5v10M13 5v10" />}
                </svg>
              </button>
            )}
          </div>
        </section>
        <Ticker />
        <Product />
        <ConnectedSpace />
        <Portal onInstall={openInstall} />
        <Faq />
      </main>
      <Footer />
      <InstallModal />
    </div>
  );
}
